import { Button, Colors, EditableText, Icon } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import { ChangeEvent, useState } from "react";
import styled from "styled-components";
import {
  Box,
  CustomCard,
  CustomSelect,
  Text,
  TitleComponent,
  useDarkMode,
} from "ui-components";
import TagsMultiSelect from "./tags";
import { HtmlRequest } from "..";
import CompanyWebsitePage from "./company-website-page";
import { UseMutationResult } from "react-query";
import {  CompanyDetailT, FormDataT, INDUSTRY_OPTIONS_OBJ, MARKET_CAP_OPTIONS_OBJ } from "../../../../utils/constants";
import BadgesMultiSelect from "./badges";
import Categories from "./categories";

const CompanyDetails = ({
  companyID,
  companyDetails,
  tagValues,
  setTagValues,
	categoryValues,
	setCategoryValues,
  changeFormData,
  getHtmlByUrlMutation,
  handleOnChange,
	badgeValues,
	setBadgeValues,
}: {
  companyID: string | undefined;
  companyDetails: CompanyDetailT;
  tagValues: string[];
  setTagValues: (e: string[]) => void;
  categoryValues:string[];
  setCategoryValues: (e: string[]) => void;
  changeFormData: (name: string, value: any) => void;
  getHtmlByUrlMutation: UseMutationResult<any, unknown, HtmlRequest, unknown>;
  handleOnChange: (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,key:string
  ) => void;
	badgeValues : (string|null)[];
	setBadgeValues: (e: (string|null)[]) => void;
}) => {
  const [darkMode] = useDarkMode();
  const [expanded, setExpanded] = useState<boolean>(true);
	const industryCurrentObj = INDUSTRY_OPTIONS_OBJ.filter((ele)=>ele.value===companyDetails.industry.industry)?.[0];
	const marketCurrentObj = MARKET_CAP_OPTIONS_OBJ.filter((ele)=>ele.value===companyDetails.industry.market_cap)?.[0];
  return (
    <CustomCard marginTop="0px" minHeight="600px">
      <Box flexDir="row" justify="between" p={"medium"} items="center">
        <TitleComponent fontSize="18px" marginTop="0px">
          Company Details
        </TitleComponent>
        <Tooltip2 content="Full content view" hoverOpenDelay={500}>
					<Button
						active={expanded}
						intent={expanded ? "primary" : "none"}
						icon={expanded ? "chevron-up" : "chevron-down"}
						onClick={()=>setExpanded(!expanded)}
					/>
				</Tooltip2>
      </Box>
      <BorderDiv />
      <DetailsBox visible={true} mb={"0px"}>
        <DetailsContent>
          <Box flexDir="row" gap="medium" mx={"small"} mt={"medium"}>
            <FlexColumn>
              <Box flexDir="row" gap={"small"} items="center">
                <TitleComponent
                  fontSize="15px"
                  marginTop="0px"
                  className="white-space-no-wrap"
                >
                  Company No.:
                </TitleComponent>
                <Text muted={true}>{companyDetails.licence_no}</Text>
              </Box>
            </FlexColumn>

            <FlexColumn>
              <Box flexDir="row" gap="small" items="center" className="min-w-0">
                <TitleComponent
                  fontSize="15px"
                  marginTop="0px"
                  className="white-space-no-wrap"
                >
                  Name:
                </TitleComponent>
                <Box
                  flexDir="row"
                  items="center"
                  gap="small"
                  className="min-w-0 flex-1 overflow-hidden"
                >
                  <TitleComponent fontSize="14px" marginTop="0px">
                    {companyDetails?.name?.length > 17
                      ? `${(companyDetails.name ).substring(0, 17)}...`
                      : (companyDetails.name )}
                  </TitleComponent>
                  {(companyDetails.name ?? "").length > 17 && (
                    <Tooltip2 content={companyDetails.name} position="top">
                      <Icon
                        icon="info-sign"
                        intent="primary"
                        className="mb-xs"
                      />
                    </Tooltip2>
                  )}
                </Box>
              </Box>
            </FlexColumn>

            <FlexColumn>
              <Box flexDir="row" gap="small" items="center">
                <TitleComponent
                  fontSize="15px"
                  marginTop="0px"
                  className="white-space-no-wrap"
                >
                  Status:
                </TitleComponent>
                <Text muted={true}>{companyDetails.status}</Text>
              </Box>
            </FlexColumn>
          </Box>
        </DetailsContent>
      </DetailsBox>

      <DetailsBox visible={expanded} mb="20px" expanded={expanded}>
        <DetailsContent>
          <Box flexDir="col" mx={"small"} gap={"medium"}>
            <Box flexDir="row" gap="medium">
              <FlexColumn>
                <Box flexDir="row" gap="small" items="center">
                  <TitleComponent fontSize="15px" marginTop="0px">
                    Incorporation Date:
                  </TitleComponent>
                  <Text muted={true}>{companyDetails.incorporation_date ?? ""}</Text>
                </Box>

                <Box
                  flexDir="row"
                  gap="small"
                  items="center"
                  className="min-w-0 flex-1"
                >
                  <TitleComponent
                    fontSize="15px"
                    marginTop="0px"
                    className="white-space-no-wrap"
                  >
                    Lsic Code:
                  </TitleComponent>
                  <EditableWrapper
                    placeholder="Enter Lsic Code..."
                    value={companyDetails.industry?.lsic_code || ""}
                    onChange={(val) =>
                      changeFormData("companyDetail.industry.lsic_code", val)
                    }
                    className={darkMode ? "muted-text" : "muted-text-opacity-1"}
                  />
                </Box>

                <Box flexDir="row" gap="small" items="center" width="1">
                  <TitleComponent fontSize="15px" marginTop="0px" marginBottom="0px">
                    Industry:
                  </TitleComponent>
                  <CustomSelect
										flexDir="row"
										isActiveItem={true}
										label={industryCurrentObj ? industryCurrentObj.label : "Select"}
										items={INDUSTRY_OPTIONS_OBJ}
										values={[industryCurrentObj ? industryCurrentObj.value : ""]}
										onSelect={(item)=> changeFormData("companyDetail.industry.industry", item.value)}
                  />
                </Box>
                <Box flexDir="row" gap="small" items="center" width="1">
                  <TitleComponent fontSize="15px" marginTop="0px" marginBottom="0px">
                    Market Capital:
                  </TitleComponent>
                  <CustomSelect
										flexDir="row"
										isActiveItem={true}
										label={marketCurrentObj ? marketCurrentObj.label : "Select"}
										items={MARKET_CAP_OPTIONS_OBJ}
										values={[marketCurrentObj ? marketCurrentObj.value : ""]}
										onSelect={(item)=> changeFormData("companyDetail.industry.market_cap", item.value)}
                  />
                </Box>
              </FlexColumn>

              <FlexColumn>
                <Box
                  flexDir="row"
                  gap="small"
                  items="center"
                  className="min-w-0"
                >
                  <TitleComponent
                    fontSize="15px"
                    marginTop="0px"
                    className="white-space-no-wrap"
                  >
                    Company Type:
                  </TitleComponent>
                  <Box
                    flexDir="row"
                    items="center"
                    gap="small"
                    className="min-w-0 flex-1 overflow-hidden"
                  >
                    <EditableWrapper
                      placeholder="Enter Company Type..."
                      value={companyDetails.company_type || ""}
                      onChange={(val) => changeFormData("companyDetail.company_type", val)}
                      className={
                        darkMode ? "muted-text" : "muted-text-opacity-1"
                      }
                    />
                    {(companyDetails.company_type ?? "").length > 13 && (
                      <Tooltip2 content={companyDetails.company_type} position="top">
                        <Icon
                          icon="info-sign"
                          intent="primary"
                          className="mb-xs"
                        />
                      </Tooltip2>
                    )}
                  </Box>
                </Box>
								
								<Categories 
									values={categoryValues}
									setValues={setCategoryValues}
									changeFormData={changeFormData}
								/>
                <Box width="1">
                  <TagsMultiSelect
                    values={tagValues}
                    setValues={setTagValues}
                    changeFormData={changeFormData}
                  />
                </Box>
                
              </FlexColumn>

              <FlexColumn>
                <Box
                  flexDir="row"
                  gap="small"
                  items="center"
                  className="min-w-0"
                >
                  <TitleComponent
                    fontSize="15px"
                    marginTop="0px"
                    className="white-space-no-wrap"
                  >
                    Sic Code:
                  </TitleComponent>
                  <Box
                    flexDir="row"
                    items="center"
                    gap="small"
                    className="min-w-0 flex-1 overflow-hidden"
                  >
                    <EditableWrapper
                      placeholder="Enter Sic Code..."
                      value={companyDetails.industry?.sic_code || ""}
                      onChange={(val) =>
                        changeFormData("companyDetail.industry.sic_code", val)
                      }
                      className={
                        darkMode ? "muted-text" : "muted-text-opacity-1"
                      }
                    />
                    {(companyDetails.industry.sic_code ?? "").length > 16 && (
                      <Tooltip2
                        content={companyDetails.industry.sic_code}
                        position="top"
                      >
                        <Icon
                          icon="info-sign"
                          intent="primary"
                          className="mb-xs"
                        />
                      </Tooltip2>
                    )}
                  </Box>
                </Box>

                <Box
                  flexDir="row"
                  gap="small"
                  items="center"
                  className="min-w-0 flex-1"
                >
                  <TitleComponent
                    fontSize="15px"
                    marginTop="0px"
                    className="white-space-no-wrap"
                  >
                    Sub Industry:
                  </TitleComponent>
                  <EditableWrapper
                    placeholder="Enter Sub Industry..."
                    value={companyDetails.industry?.sub_industry || ""}
                    onChange={(val) =>
                      changeFormData("companyDetail.industry.sub_industry", val)
                    }
                    className={darkMode ? "muted-text" : "muted-text-opacity-1"}
                  />
                </Box>
								<Box width="1">
                  <BadgesMultiSelect
                    values={badgeValues}
                    setValues={setBadgeValues}
                    changeFormData={changeFormData}
                  />
                </Box>
              </FlexColumn>
            </Box>

            <Box
              flexDir="row"
              gap={"medium"}
              mb={"medium"}
              className="min-w-0 flex-1"
            >
              <TitleComponent fontSize="15px" marginTop="0px">
                Description:
              </TitleComponent>
              <EditableText
                placeholder="Write something ..."
                value={companyDetails.description || ""}
                onChange={(val) => changeFormData("companyDetail.description", val)}
                maxLines={1}
                className={`${darkMode ? "muted-text" : "muted-text-opacity-1"} w-800 flex-grow overflow-hidden`}
              />
            </Box>
             <Box
              flexDir="row"
              gap={"medium"}
              mb={"medium"}
              className="min-w-0 flex-1"
            >
              <TitleComponent fontSize="15px" marginTop="0px">
                Business Description:
              </TitleComponent>
              <EditableText
                placeholder="Write something ..."
                value={companyDetails.business_description || ""}
                onChange={(val) => changeFormData("companyDetail.business_description", val)}
                maxLines={1}
                className={`${darkMode ? "muted-text" : "muted-text-opacity-1"} w-800 flex-grow overflow-hidden`}
              />
            </Box>
          </Box>
        </DetailsContent>
      </DetailsBox>

      <CompanyWebsitePage companyID={companyID} getHtmlByUrlMutation={getHtmlByUrlMutation} />
    </CustomCard>
  );
};

export default CompanyDetails;

const FlexColumn = styled(Box).attrs({
  flexDir: "col",
  gap: "medium",
  width: "1/3",
})`
  min-width: 0; /* critical to prevent flex children overflow */
`;

const EditableWrapper = styled(EditableText)`
  flex: 1;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DetailsContent = styled.div`
  margin-left: 20px;
  transition: none;
`;

const BorderDiv = styled.div`
  border-bottom: ${({ theme: { darkMode } }) =>
    `1px solid ${darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1}`};
  margin: 0px -18px;
`;

const DetailsBox = styled.div<{ visible: boolean; mb?: string; expanded?: boolean }>`
  overflow: hidden;
  max-height: ${({ visible }) => (visible ? "1000px" : "0")};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition:
    max-height 0.5s ease,
    opacity 0.3s ease,
    margin-top 0.3s ease;

  margin-bottom: ${({ mb }) => mb || "0px"};
  margin-top: ${({ expanded }) => (expanded ? "16px" : "0px")};
  transition-delay: ${({ expanded }) => (expanded ? "0s, 0s, 0s" : "0s, 0s, 0.5s")};
`;
